import React, { useState, useEffect, ReactNode } from 'react';
import {
  IonContent,
  IonSpinner,
  IonText,
  IonIcon,
  useIonViewWillEnter,
} from '@ionic/react';
import Toaster, { ToasterVariant } from '@/components/common/Toaster';
import { Capacitor } from '@capacitor/core';
import { alertCircleOutline, addOutline, gridOutline } from 'ionicons/icons';
import PageLayout from '@/components/navigation/PageLayout';
import { usePageTitle } from '@/contexts/PageTitleContext';
import CorrectiveActionAccordionRow from '@/components/corrective-action/desktop/CorrectiveActionAccordionRow';
import CACard from '@/components/corrective-action/mobile/CACard';
import ActionAccordion from '@/components/common/accordion/ActionAccordion';
import PageHeaderBar from '@/components/common/PageHeaderBar';
import CorrectiveActionsGridView from '@/components/corrective-action/desktop/CorrectiveActionsGridView';
import lookupDataService, { CA_VALIDATION_STATUSES, isValidToStatusId } from '@/services/data/LookupDataService';
import CorrectiveActionModal from '@/components/corrective-action/modal/CorrectiveActionModal';
import { correctiveActionService } from '@/services/CorrectiveActionService';
import { CAGridRow, CAGroup, CAFilters, CAItem, CAModalData, CAId } from '@/types/model/corrective-action';
import { apiService } from '@/services/ApiService';
import permissionService from '@/services/PermissionService';
import { LineItemUnion } from '@/types/model/common';
import { inspectionService } from '../services/InspectionService';
import caOrchestrationService from '@/services/orchestration/CAOrchestrationService';
import { ActionAccordionRowData } from '@/types/model/actionAccordion';
import { buildCorrectiveActionActionAccordionRows } from '@/services/util/adapters/CorrectiveActionActionAccordionAdapter';
import { PageHeaderIconAction } from '@/types/model/pageHeaderBar';
import {
  buildCorrectiveActionFilterControls,
  buildCorrectiveActionFilterSummary,
  mapCorrectiveActionControlsToFilters,
} from '@/services/util/adapters/CorrectiveActionsPageHeaderAdapter';
import { filterCorrectiveActionGroupsBySearch } from '@/services/util/correctiveActions/correctiveActionsSearch';
import { CAViewMode, getStoredCAViewMode, setStoredCAViewMode } from '@/services/util/correctiveActions/correctiveActionsViewPreference';

const CorrectiveActions: React.FC = () => {
  usePageTitle('Corrective Actions');
  const DEFAULT_NON_CLOSED_STATUS_IDS = [0, 1, 2, 3, 5];

  const [data, setData] = useState<CAGroup[]>([]);
  const [viewData, setViewData] = useState<CAGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [lookupReady, setLookupReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<ToasterVariant>('success');
  const [groupLineItemContentMap, setGroupLineItemContentMap] = React.useState<Record<number, Record<number, ReactNode | null>>>({});
  const [rawCAMap, setRawCAMap] = React.useState<Record<number, Record<number, { cas: CAItem[]; group: CAGroup; lineItem: LineItemUnion }>>>({}); 
  const [actionAccordionRows, setActionAccordionRows] = useState<ActionAccordionRowData[]>([]);
  const [caGridRows, setCaGridRows] = useState<CAGridRow[]>([]);
  const [viewMode, setViewMode] = useState<CAViewMode>(() => getStoredCAViewMode());

  const [expandedGroupValues, setExpandedGroupValues] = useState<string[]>([]);
  const [expandedLineItemValues, setExpandedLineItemValues] = useState<Record<number, string[]>>({});

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState<CAModalData | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const DEFAULT_ALL_VALIDATION_STATUS_IDS = CA_VALIDATION_STATUSES.map(s => s.Id);

  const [filters, setFilters] = useState<CAFilters>({
    showAll: false,
    selectedStatusIds: DEFAULT_NON_CLOSED_STATUS_IDS,
    selectedValidationStatusIds: DEFAULT_ALL_VALIDATION_STATUS_IDS,
  });

  // ── Responsive ────────────────────────────────────────────────────────────

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // ── Load data ─────────────────────────────────────────────────────────────

  const ensureLookupReady = async (): Promise<boolean> => {
    try {
      setLookupReady(false);
      await lookupDataService.initialize();
      const ready = lookupDataService.isLoaded();
      setLookupReady(ready);
      return ready;
    } catch (error) {
      console.error('Corrective Actions page lookup initialization failed:', error);
      setLookupReady(false);
      return false;
    }
  };

  const loadData = async () => {
    if (!(await ensureLookupReady())) {
      setError('Lookup data is still loading. Please try again.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setExpandedGroupValues([]);
      setExpandedLineItemValues({});
      const groups = await correctiveActionService.getGroupedCAData();
      setData(groups);
      setCaGridRows(correctiveActionService.getAllCorrectiveActionItems());
      await inspectionService.refreshLookupCache();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load corrective actions';
      setError(msg);
      setToastMessage(msg);
      setToastVariant('error');
      setShowToast(true);
      console.error('Error loading corrective actions:', err);
    } finally {
      setLoading(false);
    }
  };

  useIonViewWillEnter(() => {
    void loadData();
  });

  // ── Filter data ───────────────────────────────────────────────────────────

  useEffect(() => {
    const filtered = correctiveActionService.filterCAGroups(data, filters);
    const searched = filterCorrectiveActionGroupsBySearch(filtered, searchQuery);
    setViewData(searched);
    if (searched.length === 0 && !loading) {
      setError('No corrective actions found matching the current filters and search.');
    } else {
      setError(null);
    }
  }, [filters, data, loading, searchQuery]);

  useEffect(() => {
    setActionAccordionRows((prevRows) =>
      buildCorrectiveActionActionAccordionRows({
        groups: viewData,
        groupLineItemContentMap,
        expandedGroups: expandedGroupValues,
        expandedLineItems: expandedLineItemValues,
        previousRows: prevRows,
      })
    );
  }, [viewData, groupLineItemContentMap, expandedGroupValues, expandedLineItemValues]);

  const renderContent = () => {
    if (!lookupReady || loading) {
      return (
        <div className="loading-state">
          <IonSpinner name="crescent" />
          <IonText color="medium">
            <p>{lookupReady ? 'Loading corrective actions...' : 'Initializing lookup data...'}</p>
          </IonText>
        </div>
      );
    }

    if (error && viewData.length === 0) {
      return (
        <div className="empty-state">
          <IonIcon icon={alertCircleOutline} />
          <IonText color="medium">
            <h3>No Results Found</h3>
            <p>{error}</p>
          </IonText>
        </div>
      );
    }

    return (
      <ActionAccordion
        rows={actionAccordionRows}
        onRowsChange={setActionAccordionRows}
        onRowExpand={handleActionAccordionRowExpand}
      />
    );
  };

  
  // ── Rebuild content map whenever filters, raw data, or viewport changes ──

  useEffect(() => {
    const newContentMap: Record<number, Record<number, ReactNode | null>> = {};
    for (const [groupIdStr, lineItems] of Object.entries(rawCAMap)) {
      const groupId = Number(groupIdStr);
      newContentMap[groupId] = {};
      for (const [lineItemIdStr, { cas, group, lineItem }] of Object.entries(lineItems)) {
        const lineItemId = Number(lineItemIdStr);
        const selectedStatusSet = new Set(filters.selectedStatusIds);
        const selectedValidationSet = new Set(filters.selectedValidationStatusIds);
        const filteredCas = filters.showAll
          ? cas
          : cas.filter(ca =>
              selectedStatusSet.has(ca.statusId) &&
              selectedValidationSet.has(isValidToStatusId(ca.isValid))
            );
        newContentMap[groupId][lineItemId] = getContentForCAs(filteredCas, group, lineItem);
      }
    }
    setGroupLineItemContentMap(newContentMap);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, rawCAMap, isMobile]);

  // ── Modal handlers ────────────────────────────────────────────────────────

  const handleCAEdit = async (
    caItem: CAItem,
    groupId: number,
    groupIdentifier: number,
    groupTitle: string,
    lineItemId: number,
    lineItem: string,
    lineItemTitle: string,
  ) => {

    const completeCAItem = await caOrchestrationService.getCorrectiveActionItem(caItem.id);

    setModalData({
      isNew: false,
      groupId,
      groupIdentifier,
      groupTitle,
      lineItemId,
      lineItem,
      lineItemTitle,
      inspectionResultId: caItem.inspectionResultId,
      inspectionDate: caItem.inspectionDate,
      caItem: completeCAItem ?? caItem,
      existingResources: [],
    });
    setIsModalOpen(true);

    // Lazy-load existing resources for the modal (fire-and-forget)
    apiService.getContractFiles()
      .then(files => {
        setModalData(prev =>
          prev ? { ...prev, existingResources: files } : prev
        );
      })
      .catch(() => { /* non-critical */ });
  };

  const handleAddNew = () => {
    setModalData({
      isNew: true,
      groupId: 0,
      groupIdentifier: 0,
      groupTitle: '',
      lineItemId: 0,
      lineItem: '',
      lineItemTitle: '',
      inspectionResultId: null,
      inspectionDate: null,
      existingResources: [],
    });
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setModalData(null);
  };

  const handleModalSaved = async () => {
    handleModalClose();
    setToastMessage('Corrective action saved');
    setToastVariant('success');
    setShowToast(true);
    // FIXME! reload data on new CA creation, but ideally we should just update the relevant line item in state instead of reloading everything
    // await loadData();
  };


  // ── Line Items handlers ─────────────────────────────────────────────────────
  const HEADER_LABELS = ['Issue', 'Issued On', 'Issued By', 'CA Status', 'Validation Status', '', ''];
  const HEADER_COLS = '3fr 1.5fr 1fr 1.5fr 1fr 52px 52px';
  const getContentForCAs = (cas: CAItem[], group: CAGroup, lineItem: LineItemUnion): ReactNode => {
      if (cas.length === 0) {
          return <IonText color="medium">No corrective actions found.</IonText>;
      }
      return isMobile ?  (
        <div style={{ padding: '0.75rem' }}>
            {cas.map(ca => (
            <CACard
                key={ca.id}
                caItem={ca}
                onEdit={() => handleCAEdit(ca, group.id, group.groupIdentifier, group.title, lineItem.id, lineItem.lineItem, lineItem.title)}
            />
            ))}
        </div>
      ) : (
          <div>
            <div
                style={{
                display: 'grid',
                gridTemplateColumns: HEADER_COLS,
                gap: '0.75rem',
                padding: '0.65rem 1rem',
                background: 'var(--ion-color-light-tint)',
                fontWeight: '600',
                fontSize: '0.85rem',
                borderBottom: '1px solid var(--ion-color-light-shade)',
                }}
            >
                {HEADER_LABELS.map((lbl, idx) => (
                <div key={idx}>{lbl}</div>
                ))}
            </div>

            {cas.map((ca, ci) => (
                <CorrectiveActionAccordionRow
                key={ca.id}
                caItem={ca}
                index={ci}
                onEdit={() => handleCAEdit(ca, group.id, group.groupIdentifier, group.title, lineItem.id, lineItem.lineItem, lineItem.title)}
                />
            ))}
          </div>
      );
  };

  const handleLineItemExpand = async (lineItem: LineItemUnion) => {
    try {
        const groupId = lineItem.groupId;
        const lineItemId = lineItem.id;

        if (groupLineItemContentMap[groupId]?.[lineItemId]) {
          return;
        }

        const subItems = (lineItem as { subItems: CAId[] }).subItems ?? [];
        const cas = await correctiveActionService.getCorrectiveActionsForLineItem(groupId, lineItemId, subItems);
        const group = data.find(g => g.id === groupId);
        if (!group) {
          throw new Error(`Group with ID ${groupId} not found`);
        }
        const groupLineItemContent = getContentForCAs(cas, group, lineItem);
        setRawCAMap(prev => ({
          ...prev,
          [groupId]: { ...prev[groupId], [lineItemId]: { cas, group, lineItem } }
        }));
        setGroupLineItemContentMap(prev => ({
          ...prev,
          [groupId]: { ...prev[groupId], [lineItemId]: groupLineItemContent }
        }));
    } catch (err) {
        console.error(`Error loading CAs for line item ${lineItem.id}:`, err);
        setToastMessage('Failed to load corrective actions');
        setToastVariant('error');
        setShowToast(true);
        throw err;
    } finally {
        // no-op
    }
  };

  const handleActionAccordionRowExpand = async (row: ActionAccordionRowData) => {
    if (row.type !== 'LineItem') {
      return;
    }

    for (const group of viewData) {
      const lineItem = group.lineItems?.find((candidate) => candidate.id === row.id);
      if (lineItem) {
        await handleLineItemExpand(lineItem);
        break;
      }
    }
  };
  // ── Render helpers ────────────────────────────────────────────────────────

  const canToggleView = !isMobile && !Capacitor.isNativePlatform();
  const effectiveViewMode: CAViewMode = canToggleView ? viewMode : 'accordion';

  const handleViewModeChange = (mode: CAViewMode) => {
    setViewMode(mode);
    setStoredCAViewMode(mode);
  };

  const handleGridEdit = (row: CAGridRow) => {
    void handleCAEdit(row, row.groupId, row.groupIdentifier, row.groupTitle, row.lineItemId, row.lineItem, row.lineItemTitle);
  };

  const filterControls = buildCorrectiveActionFilterControls(filters);
  const actionIcons: PageHeaderIconAction[] = [
    ...(permissionService.canAddCA()
      ? [{
          kind: 'icon' as const,
          group: 1,
          order: 1,
          id: 'corrective-actions-new',
          label: 'New Corrective Action',
          icon: addOutline,
          requiresConfirm: false,
          onExecute: handleAddNew,
        }]
      : []),
    ...(canToggleView
      ? [{
          kind: 'icon' as const,
          group: 2,
          order: 1,
          id: 'corrective-actions-switch-to-grid',
          label: 'Switch to Grid View',
          icon: gridOutline,
          requiresConfirm: false,
          onExecute: () => handleViewModeChange('grid'),
        }]
      : []),
  ];

  // ── Page ──────────────────────────────────────────────────────────────────

  return (
    <PageLayout>

      <IonContent fullscreen className="ion-padding">
        {effectiveViewMode === 'grid' ? (
          <CorrectiveActionsGridView
            rows={caGridRows}
            onEdit={handleGridEdit}
            onAddNew={handleAddNew}
            onSwitchToAccordion={() => handleViewModeChange('accordion')}
          />
        ) : (
          <>
            <PageHeaderBar
              filters={{
                controls: filterControls,
                summaryText: buildCorrectiveActionFilterSummary(filters),
                onApply: (nextControls) => {
                  setFilters(mapCorrectiveActionControlsToFilters(nextControls, filters));
                },
              }}
              actions={{
                iconActions: actionIcons,
              }}
              search={{
                value: searchQuery,
                placeholder: 'Search groups and line items',
                debounceMs: 300,
                onSearch: setSearchQuery,
              }}
              collapsible={isMobile}
            />

            {renderContent()}
          </>
        )}

        {/* Modal */}
        <CorrectiveActionModal
          isOpen={isModalOpen}
          modalData={modalData}
          onClose={handleModalClose}
          onSaved={handleModalSaved}
        />

        {/* Toast */}
        <Toaster
          isOpen={showToast}
          message={toastMessage}
          variant={toastVariant}
          onDidDismiss={() => setShowToast(false)}
        />
      </IonContent>
    </PageLayout>
  );
};

export default CorrectiveActions;
